import { Box, Button, Tab as ChakraTabItem, Text } from '@chakra-ui/react';
import { MouseEvent, PropsWithChildren, useMemo, useRef } from 'react';

import { rem } from 'polished';
import { ButtonLink } from '../ButtonLink';
import { DisabledTabItem } from './DisabledTabItem';

export type TabsBarItemProps = {
  /** Id of the tab that matches with its content. */
  id: string;
  /** Optional extra onClick function. */
  onClick?: (e?: MouseEvent<HTMLButtonElement>) => void;
  /** Automation attribute */
  testId?: string;
  /* Tab is active. */
  isActive?: boolean;
  /* Tab is disabled. */
  isDisabled?: boolean;
  /** Count */
  count?: number;
  /** Allows you to make the component a navigation element */
  link?: LinkConnectorProps;
  /** Allows you to make the component a regular button */
  isButton?: boolean;
  isHidden?: boolean;
};

const TabsBarItem = ({
  link,
  id,
  isActive,
  isDisabled,
  onClick,
  children,
  count,
  testId = 'tab-bar-item',
  isButton,
  isHidden,
}: PropsWithChildren<TabsBarItemProps>) => {
  const tabRef = useRef<HTMLButtonElement | null>(null);

  const Component = useMemo(() => (isButton ? Button : link ? ButtonLink : ChakraTabItem), [link]);

  if (isDisabled) {
    return <DisabledTabItem>{children}</DisabledTabItem>;
  }

  return (
    <Component
      href={link?.href || ''}
      ref={tabRef}
      data-testid={testId}
      px={0}
      onClick={onClick}
      height={{ base: rem(40), md: rem(44) }}
      variant="clear"
      borderRadius="none"
      color="gray.1"
      bg="none"
      fontSize="md"
      aria-controls={id}
      aria-selected={isActive}
      isDisabled={isDisabled}
      position="relative"
      border="none"
      _hover={{
        color: 'gray.5',
      }}
      _focus={{
        outline: 'none',
        background: 'none',
      }}
      _focusVisible={{
        boxShadow: 'outline-inset',
      }}
      _active={{
        outline: 'none',
        background: 'none',
      }}
      _selected={{
        color: 'white',
      }}
      {...(isActive && { color: 'white', fontWeight: '500' })}
      _disabled={{
        cursor: 'not-allowed',
        color: 'gray.5',
      }}
      {...(isHidden && { display: 'none' })}
    >
      <Text color="inherit" noOfLines={1}>
        {children}
        {!!count && !isNaN(count) && (
          <Text as="sup" fontSize="xs" pl="space.4" data-testid={`${testId}-count`}>
            ({count})
          </Text>
        )}
      </Text>
      {isActive && !isDisabled && (
        <Box
          position="absolute"
          width="100%"
          bottom={0}
          left={0}
          zIndex={2}
          height={rem(2)}
          bg="white"
        />
      )}
    </Component>
  );
};

export { TabsBarItem };
